import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { isStaff } from '@foundry/shared';
import { useLogout, useMe } from '../api/auth.js';
import { Spinner } from '../components/ui.js';

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center">{children}</div>;
}

/** Sign the user out after this much inactivity — matches the server's session TTL. */
const IDLE_LIMIT_MS = 1000 * 60 * 15; // 15 minutes (founders)
const STAFF_IDLE_LIMIT_MS = 1000 * 60 * 30; // 30 minutes (developers/admins)

/**
 * A window event that resets the idle timer, so a deliberately-unattended view
 * (the Monitor wall/tablet display, while auto-refreshing) can signal that it is
 * live and should not be idle-logged-out. Its polling also keeps the server
 * session alive, so the two stay in step. Dispatch with
 * `window.dispatchEvent(new Event(KEEP_ALIVE_EVENT))`.
 */
export const KEEP_ALIVE_EVENT = 'foundry:activity';

/**
 * Signs the user out and returns them to /login after `limitMs` without any
 * interaction, so an unattended session does not stay open. The server enforces
 * the same window on its side; this makes the logout visible immediately rather
 * than only on the next request. Mounted only while authenticated (renders
 * nothing), so its listeners exist only when there is a session to protect.
 */
function IdleTimeout({ limitMs }: { limitMs: number }): null {
  const logout = useLogout();
  const navigate = useNavigate();

  // Keep the newest logout/navigate in a ref so the listener effect can stay
  // mounted once (empty deps) without going stale.
  const onIdle = useRef<() => void>(() => {});
  useEffect(() => {
    onIdle.current = () => {
      logout.mutate(undefined, {
        onSettled: () => {
          void navigate('/login', { replace: true });
        },
      });
    };
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const reset = (): void => {
      clearTimeout(timer);
      timer = setTimeout(() => onIdle.current(), limitMs);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    for (const e of events) window.addEventListener(e, reset, { passive: true });
    // A live auto-refreshing view (the Monitor) dispatches this to count its
    // polling as activity, so an unattended wall/tablet display stays signed in.
    window.addEventListener(KEEP_ALIVE_EVENT, reset);
    reset();
    return () => {
      clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, reset);
      window.removeEventListener(KEEP_ALIVE_EVENT, reset);
    };
  }, [limitMs]);

  return null;
}

/**
 * Gate for authenticated routes. Sends anonymous users to /login, and anyone
 * with `mustChangePassword` set to /change-password before any other route
 * (mirrors the server-side ADR-009 gate so the UI never shows a dead end).
 */
export function RequireAuth() {
  const { data: user, isPending } = useMe();
  const location = useLocation();

  if (isPending) return <Centered>{<Spinner label="Checking your session…" />}</Centered>;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return (
    <>
      <IdleTimeout limitMs={isStaff(user.role) ? STAFF_IDLE_LIMIT_MS : IDLE_LIMIT_MS} />
      <Outlet />
    </>
  );
}

/** Gate for the developer/admin console. Founders are bounced to their projects. */
export function RequireStaff() {
  const { data: user, isPending } = useMe();
  if (isPending) return <Centered>{<Spinner />}</Centered>;
  if (!user || !isStaff(user.role)) return <Navigate to="/" replace />;
  return <Outlet />;
}
